import { keepPreviousData, useQuery } from '@tanstack/react-query';

import { type CardsListCardDisputesData, cardsListCardDisputesOptions } from '@column/api';

import { useSessionStore } from '~/stores/Session';

const LIST_STALE_TIME_MS = 15_000;

export type CardDisputesListQuery = NonNullable<CardsListCardDisputesData['query']>;

export type CardDisputesListFilters = Pick<
  CardDisputesListQuery,
  'card_transaction_id' | 'stage' | 'submission_status'
>;

export interface UseCardDisputesOptions {
  query?: CardDisputesListFilters;
  cursor?: string;
  pageSize?: number;
}

export const buildCardDisputesListQuery = ({
  query,
  cursor,
  pageSize = 10,
}: UseCardDisputesOptions = {}): CardsListCardDisputesData['query'] => {
  const listQuery: CardsListCardDisputesData['query'] = { limit: pageSize };
  if (cursor) listQuery.starting_after = cursor;
  if (query?.card_transaction_id) listQuery.card_transaction_id = query.card_transaction_id;
  if (query?.stage) listQuery.stage = query.stage;
  if (query?.submission_status) listQuery.submission_status = query.submission_status;
  return listQuery;
};

export const useCardDisputes = ({ query, cursor, pageSize = 10 }: UseCardDisputesOptions = {}) => {
  const { currentPlatform } = useSessionStore();

  const listQuery = buildCardDisputesListQuery({ query, cursor, pageSize });

  const result = useQuery({
    ...cardsListCardDisputesOptions({ query: listQuery }),
    enabled: Boolean(currentPlatform?.id),
    placeholderData: keepPreviousData,
    staleTime: LIST_STALE_TIME_MS,
  });

  return {
    ...result,
    cardDisputes: result.data?.card_disputes ?? [],
    hasMore: result.data?.has_more ?? false,
  };
};
